import React, { useEffect, useReducer } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useMutation } from 'react-query'
import { decode_bbox } from "ngeohash";
import {
  DEFAULT_BOUNDARIES_POSITIONS,
  DEFAULT_ZOOM,
  getMapBoundries 
} from "../../components/common/Map/utils";
import { DetailsInterface, MarkerInterface } from "../../types";
import { useAuth } from "../auth";
import { rootReducer } from "./reducers";
import {
  clearData,
  fetchMapData,
  flyToMarkers,
  initialValues,
  RootStateContext
} from "./utils";
import { useMap } from "../map";
import { useIsMobile } from "../../hooks/useIsMobile";

export const RootProvider = ({ children } : { children: React.ReactNode }) => {
  const { mapContext } = useMap();
  const [state, dispatch] = useReducer(rootReducer, initialValues);
  const location = useLocation();
  const navigator = useNavigate();
  const { mapUrl: mapUrlAuth } = useAuth();
  const searchParams = new URLSearchParams(location.search);
  const { isMobile } = useIsMobile();

  useEffect(() => {
    if (searchParams.get("previous") && searchParams.get("appName")) {
      const previousUrl = searchParams.get("previous") || "";
      const appName = searchParams.get("appName") || "";
      dispatch({
        type: "setPreviousApp",
        data: {
          appName,
          url: previousUrl
        }
      })
    }
  }, [])

  const clearDataCallback = () => {
    clearData({ dispatch });
  }

  const { mutate, isLoading } = useMutation((props: { 
    geohash: string | null,
    myLocation?: boolean,
    initial?: boolean,
    page?: number
  }) => {
    const { geohash = null, myLocation = false, initial = false, page = 1} = props || {};
    if (mapUrlAuth && mapContext) {
      if (geohash) {
        dispatch({
          type: "setGeohash",
          data: geohash
        })
        const [minLat, minLng, maxLat, maxLng] = decode_bbox(geohash);
        const zoom = mapContext.getBoundsZoom([
          [minLat, maxLng],
          [maxLat, maxLng],
          [maxLat, minLng],
          [minLat, minLng]
        ])
        return fetchMapData({
          url: mapUrlAuth,
          boundariesPositions: {
            northEast: { lat: maxLat, lng: maxLng },
            northWest: { lat: maxLat, lng: minLng },
            southEast: { lat: minLat, lng: maxLng },
            southWest: { lat: minLat, lng: minLng },
            type: "bucket"
          },
          zoom,
          filters: searchParams,
          geohash,
          myLocation,
          isMobile,
          mapContext,
          page: page
        })
      }
      else {
        dispatch({
          type: "setGeohash",
          data: null
        })
        const {
          northEast,
          northWest,
          southEast,
          southWest
        } = getMapBoundries(mapContext);
        const zoom = mapContext.getBoundsZoom([northEast, northWest, southEast, southWest]);
        return fetchMapData({
          url: mapUrlAuth,
          boundariesPositions: initial ? DEFAULT_BOUNDARIES_POSITIONS : {
            northEast,
            northWest,
            southEast,
            southWest,
            type: "screen"
          },
          zoom,
          filters: searchParams,
          geohash,
          myLocation,
          isMobile,
          mapContext,
          page: page
        })
      }
    }
    return Promise.resolve().then(() => {
      return {
        data: {
          buckets: state.dataMarkers,
          results: state.dataList,
          searchParams: searchParams,
          geohash: geohash
        }
      }
    })
  }, {
    onSuccess: (data: {
      buckets: MarkerInterface[] | [],
      results: DetailsInterface[] | [],
      searchParams: URLSearchParams,
      geohash: string | null,
      myLocation: boolean,
      isMobile: boolean,
      page: number,
      totalPages: number
    }) => {
      if (mapUrlAuth && mapContext) {
        if (data.buckets.length > 0) {
          dispatch({
            type: "setDataMarkers",
            data: data.buckets
          })
          dispatch({
            type: "setTotalPages",
            data: data.totalPages
          })
          dispatch({
            type: "setCurrentPage",
            data: data.page
          })
          dispatch({
            type: "setDataList",
            data: data.page > 1 ? [...state.dataList, ...data.results] : data.results
          })
          if (!data.geohash && !data.isMobile) {
            dispatch({
              type: "setIsMapMoving",
              data: true,
            })
            flyToMarkers({
              buckets: data.buckets,
              mapContext,
              dispatch: dispatch
            })
            dispatch({
              type: "setRefetch",
              data: {
                shouldRefetch: false,
                geohash: null
              }
            })
          }
          else {
            dispatch({
              type: "setShowSearchInThisArea",
              data: false
            })
            dispatch({
              type: "setIsMapMoving",
              data: false,
            })
            dispatch({
              type: "setRefetch",
              data: {
                shouldRefetch: false,
                geohash: data.geohash
              }
            })
          }
        }
        else if (!data.myLocation) {
          fetchMapData({
            boundariesPositions: DEFAULT_BOUNDARIES_POSITIONS,
            zoom: DEFAULT_ZOOM,
            filters: searchParams,
            url: mapUrlAuth,
            geohash: null,
            myLocation: false,
            isMobile,
            mapContext,
            precision: "2600km",
            page: 1
          }).then((res: {
            buckets: MarkerInterface[] | [],
            results: DetailsInterface[] | [],
            searchParams: URLSearchParams,
            totalPages: number
          }) => {
            if (res.buckets.length > 0) {
              navigator({
                pathname: `${process.env.REACT_APP_INTERNAL_URL}`,
                search: searchParams.toString()
              })
              dispatch({
                type: "setDataMarkers",
                data: res.buckets
              })
              dispatch({
                type: "setDataList",
                data: res.results
              })
              dispatch({
                type: "setCurrentPage",
                data: 1
              })
              dispatch({
                type: "setTotalPages",
                data: res.totalPages
              })
              dispatch({
                type: "setIsMapMoving",
                data: true
              })
              flyToMarkers({
                buckets: res.buckets,
                mapContext,
                dispatch: dispatch
              })
              dispatch({
                type: "setRefetch",
                data: {
                  shouldRefetch: false,
                  geohash: null
                }
              })
            }
            else {
              clearDataCallback();
              dispatch({
                type: "setShowSearchInThisArea",
                data: false
              })
              dispatch({
                type: "setIsMapMoving",
                data: false
              })
              dispatch({
                type: "setRefetch",
                data: {
                  shouldRefetch: false,
                  geohash: null
                }
              })
            }
          })
        }
      }
    },
    onError: () => {
      clearDataCallback();
    }
  })

  useEffect(() => {
    if (mapContext && mapUrlAuth && state.refetch.shouldRefetch) {
      mutate({ geohash: state.geohash || state.refetch.geohash });
    }
  }, [location.search, mapContext, mapUrlAuth, state.refetch.shouldRefetch])

  const value = {
    ...state,
    isLoading: isLoading,
    rootDispatch: dispatch,
    mapRefresh: mutate,
    clearData: clearDataCallback
  }

  return (
    <RootStateContext.Provider value={value}>
      {children}
    </RootStateContext.Provider>
  )
}
